// diffLeaves.js //

// A diff cell has to say what an upload did to a value, not merely what the value became, so
// the verb travels with the value as text. Screen, tooltip, and exported workbook all encode a
// leaf through here, which is the only thing keeping the three from drifting apart.

const DIFF_LEAF_FLAG = '__diffLeaf';

export const isDiffLeaf = (value) => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
  && value[DIFF_LEAF_FLAG] === true
);

/**
 * Build a tagged leaf for one field of one row.
 *
 * @param {'set'|'add'|'overwrite'|'confirm'} kind the verb this upload applied
 * @param {object} values the operands the kind reads: `delta` for add, `newValue`/`oldValue`
 *   for set and overwrite, `value` for confirm
 */
export const makeDiffLeaf = (kind, values) => ({ [DIFF_LEAF_FLAG]: true, kind, ...values });

/**
 * Encode a leaf as the plain string shown in its cell.
 *
 * `formatValue` is the host table's own number and date formatting, passed in so a diff cell
 * reads with the same conventions as the plain cells beside it.
 */
export function encodeDiffLeaf(leaf, formatValue) {
  if (!isDiffLeaf(leaf)) return '';

  const show = (value) => (
    value === null || value === undefined || value === '' ? '' : String(formatValue(value))
  );

  switch (leaf.kind) {
    case 'add': {
      const text = show(leaf.delta);
      // A negative delta already carries its sign; a positive one has to be given one, or an
      // addition reads as an ordinary value.
      return typeof leaf.delta === 'number' && leaf.delta < 0 ? text : `+${text}`;
    }
    case 'set':
      return `→ ${show(leaf.newValue)}`;
    case 'overwrite':
      return `${show(leaf.oldValue)} → ${show(leaf.newValue)}`;
    case 'confirm':
      return `✓ ${show(leaf.value)}`;
    default:
      return '';
  }
}
